import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class MotionService {
  private animateWithWebAnimations(
    element: HTMLElement,
    keyframes: Keyframe[],
    options: number | KeyframeAnimationOptions
  ): Promise<void> {
    return new Promise((resolve) => {
      const animation = element.animate(keyframes, options);
      animation.onfinish = () => {
        resolve();
      };
    });
  }

  /**
   * Animate element entrance with scale effect
   */
  scaleIn(element: HTMLElement, duration = 0.8): Promise<void> {
    return this.animateWithWebAnimations(
      element,
      [
        { transform: 'scale(0.8)', opacity: '0' },
        { transform: 'scale(1)', opacity: '1' },
      ],
      { duration: duration * 1000, easing: 'ease-out', fill: 'forwards' }
    );
  }

  /**
   * Animate element entrance with slide up effect
   */
  slideUp(element: HTMLElement, duration = 1, delay = 0.3): Promise<void> {
    return this.animateWithWebAnimations(
      element,
      [
        { transform: 'translateY(50px)', opacity: '0' },
        { transform: 'translateY(0)', opacity: '1' },
      ],
      { duration: duration * 1000, delay: delay * 1000, easing: 'ease-out', fill: 'forwards' }
    );
  }

  /**
   * Animate line growth effect
   */
  growLine(element: HTMLElement, duration = 1.5, delay = 0.8): Promise<void> {
    return this.animateWithWebAnimations(
      element,
      [{ width: '0%' }, { width: '100%' }],
      { duration: duration * 1000, delay: delay * 1000, easing: 'ease-in-out', fill: 'forwards' }
    );
  }

  /**
   * Fade in animation
   */
  fadeIn(element: HTMLElement, duration = 0.5, delay = 1.5): Promise<void> {
    return this.animateWithWebAnimations(
      element,
      [{ opacity: '0' }, { opacity: '1' }],
      { duration: duration * 1000, delay: delay * 1000, easing: 'ease-out', fill: 'forwards' }
    );
  }

  /**
   * Stagger animation for list items
   */
  staggerIn(
    elements: HTMLElement[],
    duration = 0.6,
    staggerDelay = 0.1
  ): Promise<void> {
    const animations = elements.map((el, index) =>
      this.animateWithWebAnimations(
        el,
        [
          { transform: 'translateX(20px)', opacity: '0' },
          { transform: 'translateX(0)', opacity: '1' },
        ],
        {
          duration: duration * 1000,
          delay: index * staggerDelay * 1000,
          easing: 'ease-out',
          fill: 'forwards',
        }
      )
    );

    return Promise.all(animations).then(() => undefined);
  }

  /**
   * View transition animation - enter
   */
  viewEnter(element: HTMLElement, duration = 0.6): Promise<void> {
    return this.animateWithWebAnimations(
      element,
      [
        { opacity: '0', transform: 'translateX(20px)', filter: 'blur(10px)' },
        { opacity: '1', transform: 'translateX(0)', filter: 'blur(0px)' },
      ],
      { duration: duration * 1000, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'forwards' }
    );
  }

  /**
   * View transition animation - leave
   */
  viewLeave(element: HTMLElement, duration = 0.6): Promise<void> {
    return this.animateWithWebAnimations(
      element,
      [
        { opacity: '1', transform: 'translateX(0)', filter: 'blur(0px)' },
        { opacity: '0', transform: 'translateX(-20px)', filter: 'blur(10px)' },
      ],
      { duration: duration * 1000, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'forwards' }
    );
  }

  /**
   * Overlay transition - enter
   */
  overlayEnter(element: HTMLElement, duration = 0.3): Promise<void> {
    return this.animateWithWebAnimations(
      element,
      [{ opacity: '0' }, { opacity: '1' }],
      { duration: duration * 1000, easing: 'ease-out', fill: 'forwards' }
    );
  }

  /**
   * Overlay transition - leave
   */
  overlayLeave(element: HTMLElement, duration = 0.3): Promise<void> {
    return this.animateWithWebAnimations(
      element,
      [{ opacity: '1' }, { opacity: '0' }],
      { duration: duration * 1000, easing: 'ease-out', fill: 'forwards' }
    );
  }
}
